import { RecordOf } from 'immutable';
import { createSelector } from 'reselect';
import { isString } from 'underscore';

import { entireVideosSelector } from 'redux/modules/entities/selectors';
import { State as ReduxState } from 'redux/types';
import { EntireVideoState, Selectors, State, WrappedState } from './types';

export default function createSelectors<S extends EntireVideoState>(
  stateSelectorOrName: string | ((state: ReduxState) => WrappedState<S>),
): Selectors {
  const stateSelector = isString(stateSelectorOrName)
    ? (state: ReduxState) =>
        state.getIn([stateSelectorOrName, 'entireVideo']) as RecordOf<State>
    : (state: ReduxState) =>
        stateSelectorOrName(state).get('entireVideo') as RecordOf<State>;

  const entireVideoIdSelector = createSelector(
    stateSelector,
    state => state && state.get('entireVideoId'),
  );

  const entireUploadedVideoSelector = createSelector(
    stateSelector,
    state => state && state.get('entireUploadedVideo'),
  );

  const uploadStatusSelector = createSelector(
    stateSelector,
    state => state && state.get('uploadStatus'),
  );

  const isUploadingEntireVideoSelector = createSelector(
    stateSelector,
    state => state && state.get('isUploadingEntireVideo'),
  );

  const audioPeaksSelector = createSelector(
    stateSelector,
    state => state && state.get('audioPeaks'),
  );

  const isWaveformReadySelector = createSelector(
    [entireVideoIdSelector, entireVideosSelector],
    (entireVideoId, entireVideos) => {
      if (entireVideoId == null || !entireVideos) {
        return false;
      }

      const entireVideo = entireVideos.get(String(entireVideoId));
      const status = entireVideo?.getIn(['waveform', 'status']);

      return status === 'completed';
    },
  );

  return {
    entireVideoIdSelector,
    entireUploadedVideoSelector,
    uploadStatusSelector,
    isUploadingEntireVideoSelector,
    audioPeaksSelector,
    isWaveformReadySelector,
  };
}
